import {connect} from 'react-redux';
import {type ActionCreatorsMapObject, bindActionCreators, type Dispatch} from 'redux';

import {getTeamStats} from 'mattermost-redux/actions/teams';
import {autocompleteUsers, getProfilesNotInChannel} from 'mattermost-redux/actions/users';
import {getUserStatuses} from 'mattermost-redux/selectors/entities/users';
import {type Action, type ActionResult} from 'mattermost-redux/types/actions';
import {type UserProfile} from 'mattermost-redux/types/users';
import {getConfig} from 'mattermost-redux/selectors/entities/general';
import {haveICurrentTeamPermission} from 'mattermost-redux/selectors/entities/roles';
import {getCurrentTeam, getTeam} from 'mattermost-redux/selectors/entities/teams';
import {Permissions} from 'mattermost-redux/constants';

import {addUsersToChannel} from 'actions/channel_actions';
import {loadStatusesForProfilesList} from 'actions/status_actions';

import {closeModal} from 'actions/views/modals';

import {type GlobalState} from 'types/store';

import {openInsufficientPermissionsModal} from 'actions/views/open_insufficient_permissions_modal';

import ChannelInviteModal from './channel_invite_modal';

type OwnProps = {
    channelId?: string;
    teamId?: string;
    hideInvitationLink?: boolean;
}

function isInvitationLinkVisible(state: GlobalState, ownProps: OwnProps) {
    if (ownProps.hideInvitationLink) {
        return false;
    }

    const config = getConfig(state);
    const emailInvitationsEnabled = config.EnableEmailInvitations === 'true';

    if (!emailInvitationsEnabled) {
        return false;
    }

    const currentTeam = ownProps.teamId ? getTeam(state, ownProps.teamId) : getCurrentTeam(state);

    if (currentTeam?.group_constrained) {
        return false;
    }

    const guestsEnabled = config.EnableRestrictedGuestAccounts === 'true';
    const guestsPlusEnabled = config.EnableGuestAccounts === 'true';
    const guestAccountsEnabled = guestsEnabled || guestsPlusEnabled;

    const canAddMembers = haveICurrentTeamPermission(state, Permissions.ADD_USER_TO_TEAM);
    const canInviteGuests = guestAccountsEnabled && haveICurrentTeamPermission(state, Permissions.INVITE_GUEST);

    return canAddMembers || canInviteGuests;
}

function makeMapStateToProps() {
    return (state: GlobalState, props: OwnProps) => {
        const userStatuses = getUserStatuses(state);

        return {
            isInvitationLinkVisible: isInvitationLinkVisible(state, props),
            userStatuses,
        };
    };
}

export type ChannelInviteModalActions = {
    addUsersToChannel: (channelId: string, userIds: string[]) => Promise<ActionResult>;
    getProfilesNotInChannel: (
        teamId: string,
        channelId: string,
        groupConstrained: boolean,
        page: number,
        perPage?: number
    ) => Promise<ActionResult>;
    getTeamStats: (teamId: string) => void;
    loadStatusesForProfilesList: (users: UserProfile[]) => void;
    autocompleteUsers: (props: {term: string; teamId: string; channelId: string; options?: {
        limit?: number;
        boost?: boolean;
    };}) => Promise<ActionResult>;
    closeModal: (modalId: string) => void;
    openInsufficientPermissionsModal: (value: 'ADD_TO_CHANNEL') => void;
}

function mapDispatchToProps(dispatch: Dispatch) {
    return {
        actions: bindActionCreators<ActionCreatorsMapObject<Action>, ChannelInviteModalActions>({
            addUsersToChannel,
            getProfilesNotInChannel,
            getTeamStats,
            loadStatusesForProfilesList,
            autocompleteUsers,
            closeModal,
            openInsufficientPermissionsModal,
        }, dispatch),
    };
}

export default connect(makeMapStateToProps, mapDispatchToProps)(ChannelInviteModal);
